import React, { useCallback, useContext, useEffect, useState } from 'react';
import TestCaseService from '@modules/test-development/services/test-case.service';
import { TestCase } from '@modules/test-development/models/test-case.model';
import { TestCaseCreateRequest } from '@modules/test-development/models/test-case-create.model';

import { PrimaryButton, toastify, useTranslation } from '@testsigma/atoms';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import AmplitudeEventService from '@services/amplitude-event.service';
import { TestDevelopmentContext } from '@modules/test-development/contexts/test-development-context';
import { VersionContext } from '@modules/test-development/contexts/version.context';
import { logErrorToSentry } from '@utils/common';
import { AxiosError } from 'axios';

interface CreateTestCaseButtonProps {
  isTestCase?: boolean;
  isOpenInNewTab?: boolean;
}

export const useCreateNewTestCase = (
  isTestCase,
  canSendAmplitudeEvent = true,
  isOpenInNewTab = false,
) => {
  const navigate = useNavigate();

  const [isStepGroup, setIsStepGroup] = useState<boolean>(false);
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.includes('/cases/') || isTestCase) {
      setIsStepGroup(false);
    } else {
      setIsStepGroup(true);
    }
  }, [pathname, isTestCase]);

  const userPreferenceContext = useContext(UserPreferenceContext);
  const testDevelopmentContext = useContext(TestDevelopmentContext);
  const versionContext = useContext(VersionContext);

  const userPreference = userPreferenceContext?.userPreference;
  const { versionId } = userPreference?.preference?.projectPreference;
  const [isCreating, setIsCreating] = useState(false);

  const version = testDevelopmentContext?.version || versionContext?.version;

  const createNewTestCase = useCallback(
    (navigateHash = '') => {
      setIsCreating(true);
      const payload = getPayload();
      TestCaseService.create(payload)
        .then((testCase: TestCase) => {
          sendAmplitudeEventOnCreate(testCase);
          goToStepsPageWithCreate(testCase.id, navigateHash);
        })
        .catch((error: AxiosError) => {
          toastify(error?.response?.data?.error || error?.message, {
            type: 'error',
            hideProgressBar: false,
          });
          logErrorToSentry({ error });
        })
        .finally(() => {
          setIsCreating(false);
        });
    },
    [isStepGroup, versionId],
  );

  const getPayload = () => {
    const request = new TestCaseCreateRequest();
    request.applicationVersionId = versionId;
    request.isStepGroup = isStepGroup;
    return request;
  };

  const goToStepsPageWithCreate = (testCaseId, navigateHash) => {
    const testcasePath = isStepGroup ? 'step-groups' : 'cases';
    const url = `/td/${testcasePath}/${testCaseId}?isCreate=true${navigateHash}`;
    if (isOpenInNewTab) {
      window.open(`/ui${url}`, '_blank');
    } else {
      navigate(url);
    }
  };

  const sendAmplitudeEventOnCreate = (testCase: TestCase) => {
    if (!canSendAmplitudeEvent) return;

    AmplitudeEventService.clickedCreateTestCase(
      testCase.id,
      version?.application?.applicationType,
    );
  };

  return { isCreating, createNewTestCase, isStepGroup };
};

export const CreateTestCaseButton: React.FC<CreateTestCaseButtonProps> = ({
  isTestCase,
  isOpenInNewTab = false,
}) => {
  const { t } = useTranslation();

  const { isCreating, createNewTestCase, isStepGroup } = useCreateNewTestCase(
    isTestCase,
    isOpenInNewTab,
  );

  return (
    <PrimaryButton
      isDisabled={isCreating}
      onClick={createNewTestCase}
      strollr-unique-id='create-testcase-button'
      strollr-flow-close='user-journey-sample-common-app-show-testcase-list-action-#2'
    >
      {t(isStepGroup ? 'step-group-list.create' : 'test-case-list.create')}
    </PrimaryButton>
  );
};
